import type { ActionContext, Module } from 'vuex';
import { getCrmRequests } from '../api/api';
import type {
    CrmAccessGroup,
    CrmMember,
    MemberAccess,
    MemberAccessPayload,
    OpenOpportunityCount,
    PipelineAccessListRes,
    PipelineMembersPayload,
} from '../interfaces/pipelineAccess';

/**
 * Nav: **CRM access** (AQ-11066) — who sees which pipeline, and the pipeline the CRM opens on for each person.
 *
 * Addresses as `crm/crmAccess/<action>`, state at `state.crm.crmAccess`. Every save covers one member or one
 * pipeline and commits what the server returned, so the two tabs always show the saved state.
 */
export interface CrmAccessState {
    /** The team the lists below belong to: switching team clears them before the next fetch. */
    teamId: string | null;
    members: CrmMember[];
    /** By member id. A member without an entry has no pipeline. */
    access: Record<string, MemberAccess>;
    /** Open opportunities by `${member}:${configuration}`. */
    openOpportunities: Record<string, number>;
    accessGroups: CrmAccessGroup[];
    loading: boolean;
}

const defaultState = (): CrmAccessState => ({
    teamId: null,
    members: [],
    access: {},
    openOpportunities: {},
    accessGroups: [],
    loading: false,
});

const ownedKey = (memberId: string, configurationId: string): string => `${memberId}:${configurationId}`;

type Context = ActionContext<CrmAccessState, unknown>;

export const crmAccessModule: Module<CrmAccessState, unknown> = {
    namespaced: true,
    state: defaultState,
    mutations: {
        START_TEAM(state: CrmAccessState, teamId: string): void {
            if (state.teamId !== teamId) {
                Object.assign(state, defaultState(), { teamId });
            }
        },
        SET_MEMBERS(state: CrmAccessState, members: CrmMember[]): void {
            state.members = members;
        },
        SET_ACCESS_LIST(state: CrmAccessState, { members, openOpportunities }: PipelineAccessListRes): void {
            state.access = Object.fromEntries(members.map((row) => [row.member, row]));
            state.openOpportunities = Object.fromEntries(openOpportunities
                .map(({ member, configuration, count }: OpenOpportunityCount) => [ownedKey(member, configuration), count]));
        },
        UPSERT_MEMBER_ACCESS(state: CrmAccessState, row: MemberAccess): void {
            state.access = { ...state.access, [row.member]: row };
        },
        SET_ACCESS_GROUPS(state: CrmAccessState, groups: CrmAccessGroup[]): void {
            state.accessGroups = groups;
        },
        SET_LOADING(state: CrmAccessState, value: boolean): void {
            state.loading = value;
        },
    },
    actions: {
        async fetch({ commit }: Context, { teamId }: { teamId: string }): Promise<void> {
            commit('START_TEAM', teamId);
            commit('SET_LOADING', true);
            try {
                const api = getCrmRequests().pipelineAccess;
                const [members, list, groups] = await Promise.all([
                    api.listMembers(teamId),
                    api.list(),
                    api.listAccessGroups(),
                ]);
                commit('SET_MEMBERS', members);
                commit('SET_ACCESS_LIST', list);
                commit('SET_ACCESS_GROUPS', groups);
            } finally {
                commit('SET_LOADING', false);
            }
        },
        /** Rejects on failure, so the modal stays open with the person's changes. */
        async saveMember(
            { commit }: Context,
            { memberId, payload }: { memberId: string; payload: MemberAccessPayload },
        ): Promise<void> {
            commit('UPSERT_MEMBER_ACCESS', await getCrmRequests().pipelineAccess.setMember(memberId, payload));
        },
        async savePipeline(
            { commit }: Context,
            { configurationId, payload }: { configurationId: string; payload: PipelineMembersPayload },
        ): Promise<void> {
            commit('SET_ACCESS_LIST', await getCrmRequests().pipelineAccess.updatePipeline(configurationId, payload));
        },
    },
    getters: {
        members: (state: CrmAccessState): CrmMember[] => state.members,
        accessOf: (state: CrmAccessState) => (memberId: string): MemberAccess | null => state.access[memberId] ?? null,
        accessGroups: (state: CrmAccessState): CrmAccessGroup[] => state.accessGroups,
        openOpportunities: (state: CrmAccessState) => (memberId: string, configurationId: string): number =>
            state.openOpportunities[ownedKey(memberId, configurationId)] ?? 0,
        loading: (state: CrmAccessState): boolean => state.loading,
    },
};
